import { useMemo } from "react";
import { FolderKanban, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Section, KpiGrid } from "./CommonUI";

const WSTATUS_COLORS = {
  Planning: "#06b6d4",
  Development: "#0ea5e9",
  Testing: "#8b5cf6",
  Live: "#10b981",
  Maintenance: "#f59e0b",
  Hold: "#eab308",
  Completed: "#059669",
  Cancelled: "#e11d48",
  Unknown: "#a1a1aa",
};
const CR_STATUS_COLORS = {
  Open: "#0ea5e9", "In Progress": "#f59e0b", Done: "#10b981",
  Blocked: "#e11d48", Rejected: "#a1a1aa",
};
const CR_SEV_COLORS = { Critical: "#e11d48", Major: "#f97316", Minor: "#3b82f6" };

function bucketByStatus(rows, key, palette) {
  const counts = {};
  rows.forEach((r) => {
    const v = r[key] || "Unknown";
    counts[v] = (counts[v] || 0) + 1;
  });
  return Object.entries(counts).map(([name, value]) => ({ name, value, fill: palette[name] || "#a1a1aa" }));
}

function normalizeWebsiteStatus(s) {
  const map = {
    "Development Server": "Development", "Testing Server": "Testing",
    "Closed": "Cancelled", "Maintainence": "Maintenance", "In-Live": "Live",
  };
  return map[s] || s || "Unknown";
}

function ChartCard({ title, subtitle, children, onCta, ctaLabel }) {
  return (
    <div className="bg-white border border-black/10 rounded-md p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-sm font-bold uppercase tracking-[0.15em] text-[#0a0a0a]">{title}</h4>
          {subtitle && <p className="text-xs text-[#4a4a4a] mt-0.5">{subtitle}</p>}
        </div>
        {onCta && (
          <button onClick={onCta} className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#ff5722] hover:text-[#0a0a0a]">
            {ctaLabel}
          </button>
        )}
      </div>
      <div className="h-56">{children}</div>
    </div>
  );
}

function DonutChart({ data, emptyMsg }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <div className="h-full flex items-center justify-center text-xs text-[#9a9a9a] italic">{emptyMsg}</div>;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
          {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
        </Pie>
        <RTooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function BarByCategory({ data }) {
  if (data.length === 0) return <div className="h-full flex items-center justify-center text-xs text-[#9a9a9a] italic">No data yet.</div>;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
        <RTooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => <Cell key={i} fill={d.fill || "#ff5722"} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function ClientDashboard({ projects, changeRequests, loading, onRefresh, onGoToTab }) {
  const projStatusData = useMemo(() => {
    const normed = projects.map((p) => ({ website_status: normalizeWebsiteStatus(p.website_status) }));
    return bucketByStatus(normed, "website_status", WSTATUS_COLORS);
  }, [projects]);

  const crStatusData = useMemo(() => bucketByStatus(changeRequests, "status", CR_STATUS_COLORS), [changeRequests]);
  const crSevData = useMemo(() => bucketByStatus(changeRequests, "severity", CR_SEV_COLORS), [changeRequests]);

  // Build vs Enhance breakdown
  const buildProjects   = useMemo(() => projects.filter((p) => (p.worktype || "") === "Build"),   [projects]);
  const enhanceProjects = useMemo(() => projects.filter((p) => (p.worktype || "") === "Enhance"), [projects]);
  const buildStatusData = useMemo(() => bucketByStatus(
    buildProjects.map((p) => ({ website_status: normalizeWebsiteStatus(p.website_status) })),
    "website_status", WSTATUS_COLORS,
  ), [buildProjects]);
  const enhanceStatusData = useMemo(() => bucketByStatus(
    enhanceProjects.map((p) => ({ website_status: normalizeWebsiteStatus(p.website_status) })),
    "website_status", WSTATUS_COLORS,
  ), [enhanceProjects]);

  const openCRs = changeRequests.filter((c) => c.status !== "Done" && c.status !== "Rejected").length;
  const liveCount = projects.filter((p) => normalizeWebsiteStatus(p.website_status) === "Live").length;

  return (
    <div data-testid="client-dashboard">
      <Section title="Overview" subtitle="Your projects and change requests at a glance." onRefresh={onRefresh} loading={loading}>
        <KpiGrid cards={[
          { label: "Total projects",     value: projects.length,               tone: "bg-blue-500",    icon: <FolderKanban className="h-4 w-4" /> },
          { label: "Live projects",      value: liveCount,                     tone: "bg-emerald-500", icon: <CheckCircle2 className="h-4 w-4" /> },
          { label: "Change requests",    value: changeRequests.length,         tone: "bg-orange-500",  icon: <AlertTriangle className="h-4 w-4" /> },
          { label: "Open CRs",           value: openCRs,                       tone: "bg-rose-500",    icon: <Clock className="h-4 w-4" /> },
        ]} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Projects by status" subtitle="Where each project stands right now."
            onCta={() => onGoToTab && onGoToTab("projects")} ctaLabel="Open projects →">
            <DonutChart data={projStatusData} emptyMsg="No projects yet." />
          </ChartCard>
          <ChartCard title="Change requests · status" subtitle="Open, in-progress and closed CRs."
            onCta={() => onGoToTab && onGoToTab("change-requests")} ctaLabel="Open CRs →">
            <DonutChart data={crStatusData} emptyMsg="No change requests yet." />
          </ChartCard>
          <ChartCard title="Change requests · severity" subtitle="What kind of changes have been raised.">
            <BarByCategory data={crSevData} />
          </ChartCard>
          <ChartCard title="Project types" subtitle="Breakdown by delivery group.">
            <BarByCategory data={bucketByStatus(projects.map((p) => ({ project_type: p.project_type || "Other" })), "project_type", {})} />
          </ChartCard>
        </div>
      </Section>

      {/* Build vs Enhance section — visible only when at least one project has a worktype set */}
      {(buildProjects.length + enhanceProjects.length) > 0 && (
        <div className="mt-6">
          <Section title="Build vs Enhance" subtitle="Breakdown of new builds and ongoing enhancements.">
            <KpiGrid cards={[
              { label: "Build projects",         value: buildProjects.length,                                    tone: "bg-blue-500",    icon: <FolderKanban className="h-4 w-4" />, accent: "text-blue-800" },
              { label: "Build · live",           value: buildProjects.filter((p) => normalizeWebsiteStatus(p.website_status) === "Live").length,   tone: "bg-emerald-500", icon: <CheckCircle2 className="h-4 w-4" /> },
              { label: "Enhance projects",       value: enhanceProjects.length,                                  tone: "bg-fuchsia-500", icon: <FolderKanban className="h-4 w-4" />, accent: "text-fuchsia-800" },
              { label: "Enhance · live",         value: enhanceProjects.filter((p) => normalizeWebsiteStatus(p.website_status) === "Live").length, tone: "bg-emerald-500", icon: <CheckCircle2 className="h-4 w-4" /> },
            ]} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ChartCard title="Build projects · status" subtitle={`${buildProjects.length} project${buildProjects.length === 1 ? "" : "s"} being built from scratch.`}>
                <DonutChart data={buildStatusData} emptyMsg="No Build projects yet." />
              </ChartCard>
              <ChartCard title="Enhance projects · status" subtitle={`${enhanceProjects.length} ongoing enhancement engagement${enhanceProjects.length === 1 ? "" : "s"}.`}>
                <DonutChart data={enhanceStatusData} emptyMsg="No Enhance projects yet." />
              </ChartCard>
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}
